import {
  ActivityWithDetails,
  AttachmentWithContext,
  AuditStatus,
  CommentWithContext,
  Plan,
  Record,
  ReleaseStatus,
  UserRole,
} from '@fsp/types';
import {
  AddCircleOutline,
  AssignmentTwoTone,
  AutoAwesome,
  CheckCircle,
  Error,
  FactCheck,
  FilterList,
  MenuBookTwoTone,
  NotificationsActive,
  PanoramaFishEyeOutlined,
  RocketLaunchOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AiButton from '../../components/AiButton';
import AnchoredImage from '../../components/AnchoredImage';
import ExpiringAttachments from '../../components/ExpiringAttachments';
import LoadingComponent from '../../components/LoadingComponent';
import PaperSection from '../../components/PaperSection';
import StatCard from '../../components/StatCard';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationsContext';
import ContentWrapper from '../../layout/ContentWrapper';
import { fetchOrganisationActivities } from '../../services/activity';
import { fetchExpiringAttachments } from '../../services/attachment';
import { fetchAudits } from '../../services/audit';
import { listOrganisationRecentComments } from '../../services/comment';
import { fetchPlans } from '../../services/plan';
import { fetchRecords } from '../../services/record';
import { fetchRecordCollections } from '../../services/recordCollection';
import { fetchReleases } from '../../services/release';
import { formatRelativeTime } from '@fsp/shared';

// Interface for record with collection info
interface RecordWithCollection {
  record: Record;
  collectionName: string;
  planId: string;
  planName: string;
}

export const Dashboard: React.FC = () => {
  const { organisationId, user, currentOrganisation } = useAuth();
  const isAuditor = currentOrganisation?.role === UserRole.AUDITOR;
  const { notifications } = useNotifications();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [stats, setStats] = useState({
    totalPlans: 0,
    pendingReleases: 0,
    openAudits: 0,
    recentActivities: [] as ActivityWithDetails[],
  });
  const [recentComments, setRecentComments] = useState<CommentWithContext[]>([]);
  const [recentRecords, setRecentRecords] = useState<RecordWithCollection[]>([]);
  const [expiringAttachments, setExpiringAttachments] = useState<AttachmentWithContext[]>([]);
  const [showFailedOnly, setShowFailedOnly] = useState(false);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [selectedPlanForAudit, setSelectedPlanForAudit] = useState<string>('');

  // Load dashboard data
  useEffect(() => {
    if (organisationId) {
      loadDashboardData();
    }
  }, [organisationId]);

  // Re-fetch records when filter changes
  useEffect(() => {
    if (organisationId && plans.length > 0 && !isAuditor) {
      loadRecentRecords(organisationId, plans);
    }
  }, [showFailedOnly]);

  const loadRecentRecords = async (orgId: string, plansList: Plan[]) => {
    const allRecordsWithInfo: RecordWithCollection[] = [];
    await Promise.all(
      plansList.map(async (plan) => {
        try {
          const collections = await fetchRecordCollections(orgId, plan.id);
          await Promise.all(
            collections.map(async (collection) => {
              try {
                const { records } = await fetchRecords(orgId, plan.id, collection.id, {
                  limit: 10,
                });
                records.forEach((record) => {
                  // Apply filter based on showFailedOnly
                  if (!showFailedOnly || record.passFailStatus === 'fail') {
                    allRecordsWithInfo.push({
                      record,
                      collectionName: collection.name,
                      planId: plan.id,
                      planName: plan.name,
                    });
                  }
                });
              } catch (err) {
                console.error(`Failed to fetch records for collection ${collection.id}:`, err);
              }
            })
          );
        } catch (err) {
          console.error(`Failed to fetch collections for plan ${plan.id}:`, err);
        }
      })
    );

    // Sort by submitted date and take the most recent
    allRecordsWithInfo.sort((a, b) => {
      const dateA = new Date(a.record.submittedAt).getTime();
      const dateB = new Date(b.record.submittedAt).getTime();
      return dateB - dateA;
    });
    setRecentRecords(allRecordsWithInfo.slice(0, 20));
  };

  const loadDashboardData = async () => {
    if (!organisationId) return;

    try {
      setLoading(true);

      // Fetch plans
      const plansList = await fetchPlans(organisationId);
      setPlans(plansList);

      // Count pending releases across all plans
      let pendingReleasesCount = 0;
      let openAuditsCount = 0;

      await Promise.all(
        plansList.map(async (plan) => {
          try {
            // Fetch releases for each plan
            const releases = await fetchReleases(organisationId, plan.id);
            const pendingReleases = releases.filter(
              (r) => r.status === ReleaseStatus.DRAFT || r.status === ReleaseStatus.PENDING_APPROVAL
            );
            pendingReleasesCount += pendingReleases.length;

            // Fetch audits for each plan
            const audits = await fetchAudits(organisationId, plan.id);
            const openAudits = audits.filter((a) => a.status === AuditStatus.IN_PROGRESS);
            openAuditsCount += openAudits.length;
          } catch (err) {
            console.error(`Failed to fetch data for plan ${plan.id}:`, err);
          }
        })
      );

      // Fetch expiring attachments
      const expiring = await fetchExpiringAttachments(organisationId, 30);
      setExpiringAttachments(expiring);

      // Fetch recent comments
      const comments = await listOrganisationRecentComments(organisationId);
      setRecentComments(comments);

      let activities: ActivityWithDetails[] = [];
      if (!isAuditor) {
        // Fetch recent activities
        activities = await fetchOrganisationActivities(organisationId, 10);

        // Fetch recent records - will be called separately when filter changes
        await loadRecentRecords(organisationId, plansList);
      }

      setStats({
        totalPlans: plansList.length,
        pendingReleases: pendingReleasesCount,
        openAudits: openAuditsCount,
        recentActivities: activities,
      });
    } catch (err: any) {
      console.error('Error loading dashboard:', err);
      enqueueSnackbar(err.message || 'Failed to load dashboard data', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePlanClick = async (plan: Plan) => {
    if (!organisationId) return;

    try {
      const releases = await fetchReleases(organisationId, plan.id);
      const latestApprovedRelease = releases
        .filter((r) => r.status === ReleaseStatus.APPROVED)
        .sort(
          (a, b) => new Date(b.releasedAt || 0).getTime() - new Date(a.releasedAt || 0).getTime()
        )[0];

      if (latestApprovedRelease) {
        navigate(`/app/plans/${plan.id}/releases/${latestApprovedRelease.id}`);
      } else {
        navigate(`/app/plans/${plan.id}/new/chapters`);
      }
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to open plan', { variant: 'error' });
      navigate(`/app/plans/${plan.id}/releases`);
    }
  };

  const handleStartAudit = () => {
    if (plans.length === 0) {
      enqueueSnackbar('Please create a plan first', { variant: 'info' });
      return;
    }
    setSelectedPlanForAudit(plans[0].id);
    setAuditDialogOpen(true);
  };

  const handleAuditDialogClose = () => {
    setAuditDialogOpen(false);
    setSelectedPlanForAudit('');
  };

  const handleAuditDialogConfirm = () => {
    if (selectedPlanForAudit) {
      navigate(`/app/plans/${selectedPlanForAudit}/audits/create`);
    }
  };

  if (loading) {
    return (
      <ContentWrapper>
        <LoadingComponent />
      </ContentWrapper>
    );
  }

  const handleCommentClick = (comment: CommentWithContext) => {
    if (!comment.planId || !comment.chapterId) {
      console.error('Comment missing navigation context');
      return;
    }

    const { planId, chapterId, commentableType, commentableId } = comment;

    switch (commentableType) {
      case 'page':
        // Navigate to page content editor
        navigate(`/app/plans/${planId}/new/chapters/${chapterId}/pages/${commentableId}/content`);
        break;
      case 'hazard':
        // Navigate to hazard editor
        navigate(`/app/plans/${planId}/new/chapters/${chapterId}/hazards/${commentableId}`);
        break;
      case 'ccp':
        // Navigate to CCP editor
        navigate(`/app/plans/${planId}/new/chapters/${chapterId}/ccps/${commentableId}`);
        break;
      default:
        console.error(`Unknown commentable type: ${commentableType}`);
    }
  };

  const recentPlans = plans.slice(0, 3);
  const actionableNotifications = notifications
    .filter((n) => n.severity === 'error' || n.severity === 'warning')
    .slice(0, 3);

  // Filtered records are fetched based on showFailedOnly state
  const displayedRecords = recentRecords;

  // Check recent 5 records for pass/fail status
  const recentFiveRecords = recentRecords.slice(0, 5);
  const passedCount = recentFiveRecords.filter((r) => r.record.passFailStatus === 'pass').length;
  const totalRecentCount = recentFiveRecords.length;
  const hasFailedRecords = recentFiveRecords.some((r) => r.record.passFailStatus === 'fail');

  return (
    <ContentWrapper>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Welcome back, {user?.firstName || 'User'}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Here's an overview of your food safety management system
        </Typography>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MenuBookTwoTone sx={{ fontSize: 40 }} />}
            label="Active Plans"
            value={stats.totalPlans}
            color="primary.main"
            onClick={() => navigate('/app/plans')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<RocketLaunchOutlined sx={{ fontSize: 40 }} />}
            label="Pending Releases"
            value={stats.pendingReleases}
            color="warning.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<AssignmentTwoTone sx={{ fontSize: 40 }} />}
            label="Open Audits"
            value={stats.openAudits}
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<NotificationsActive sx={{ fontSize: 40 }} />}
            label="Recent Records"
            value={totalRecentCount > 0 ? `${passedCount}/${totalRecentCount} passed` : '0'}
            color={hasFailedRecords ? 'error.main' : 'success.main'}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Expiring Documents */}
        {expiringAttachments.length > 0 && (
          <Grid item xs={12}>
            <PaperSection title="Expiring & Expired Documents" variant="default">
              <ExpiringAttachments attachments={expiringAttachments} />
            </PaperSection>
          </Grid>
        )}
        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <PaperSection title="Quick Actions" variant="default">
            <Stack spacing={2}>
              {(currentOrganisation?.role === UserRole.OWNER || currentOrganisation?.role === UserRole.ADMIN) && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<AddCircleOutline />}
                    onClick={() => navigate('/app/plans/create')}
                    fullWidth
                  >
                    Create New Plan
                  </Button>
                  <AiButton
                    variant="outlined"
                    startIcon={<AutoAwesome />}
                    onClick={() => navigate('/app/plans/create-ai')}
                    fullWidth
                  >
                    Create with AI Wizard
                  </AiButton>
                </>
              )}
              <Button
                variant="outlined"
                startIcon={<MenuBookTwoTone />}
                onClick={() => navigate('/app/plans')}
                fullWidth
              >
                View All Plans
              </Button>
              <Button
                variant="outlined"
                startIcon={<FactCheck />}
                onClick={handleStartAudit}
                fullWidth
              >
                Start Internal Audit
              </Button>
            </Stack>
          </PaperSection>
        </Grid>

        {/* Recent Comments */}
        <Grid item xs={12} md={8} sx={{ zIndex: 1 }}>
          <PaperSection title="Recent Comments" variant="default">
            {recentComments.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No recent comments
                </Typography>
              </Box>
            ) : (
              <>
                <AnchoredImage
                  src="/images/chat.svg"
                  width="600px"
                  bottom={-100}
                  right={-120}
                  opacity={1}
                  zIndex={2}
                />
                <List sx={{ pt: 0 }} dense>
                  {recentComments.slice(0, 5).map((comment, index) => (
                    <React.Fragment key={comment.id}>
                      <ListItem
                        alignItems="flex-start"
                        sx={{
                          px: 1,
                          py: 1,
                          mx: -1,
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                        onClick={() => handleCommentClick(comment)}
                      >
                        <ListItemAvatar sx={{ minWidth: 40 }}>
                          <Avatar
                            src={comment.user?.avatarUrl || undefined}
                            alt={`${comment.user?.firstName || ''} ${comment.user?.lastName || ''}`}
                            sx={{ width: 32, height: 32 }}
                          >
                            {comment.user?.firstName?.[0] || 'U'}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ lineHeight: 1.4 }} noWrap>
                              {comment.content}
                            </Typography>
                          }
                          secondary={
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Typography variant="caption" color="text.secondary">
                                {comment.user?.firstName} {comment.user?.lastName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                •
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatRelativeTime(comment.createdAt)}
                              </Typography>
                            </Stack>
                          }
                        />
                      </ListItem>
                      {index < recentComments.slice(0, 5).length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              </>
            )}
          </PaperSection>
        </Grid>

        {/* Recent Plans */}
        {/* <Grid item xs={12} md={4}>
          <PaperSection title="Recent Plans" variant="default">
            {recentPlans.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No plans yet. Create your first plan to get started!
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {recentPlans.map((plan) => (
                  <Card key={plan.id} variant="outlined">
                    <CardActionArea onClick={() => handlePlanClick(plan)}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                              {plan.name}
                            </Typography>
                            {plan.description && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              >
                                {plan.description}
                              </Typography>
                            )}
                          </Box>
                          <Chip
                            label={plan.currentReleaseId ? 'Approved' : 'Draft'}
                            color={plan.currentReleaseId ? 'success' : 'warning'}
                            size="small"
                          />
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
                {plans.length > 3 && (
                  <Button variant="text" onClick={() => navigate('/app/plans')} fullWidth>
                    View All Plans ({plans.length})
                  </Button>
                )}
              </Stack>
            )}
          </PaperSection>
        </Grid> */}

        {/* Recent Activity */}
        {!isAuditor && <Grid item xs={12} md={6}>
          <PaperSection title="Recent Activity" variant="default">
            {stats.recentActivities.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No recent activity
                </Typography>
              </Box>
            ) : (
              <List sx={{ pt: 0 }} dense>
                {stats.recentActivities.map((item, index) => (
                  <React.Fragment key={item.activity.id}>
                    <ListItem alignItems="flex-start" sx={{ px: 0, py: 1 }}>
                      <ListItemAvatar sx={{ minWidth: 40 }}>
                        <Avatar
                          src={item.user?.avatarUrl || undefined}
                          alt={
                            item.user
                              ? `${item.user.firstName || ''} ${item.user.lastName || ''}`
                              : 'System'
                          }
                          sx={{ width: 32, height: 32 }}
                        >
                          {item.user?.firstName?.[0] || 'S'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                            {item.activity.description}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {formatRelativeTime(item.activity.createdAt)}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {index < stats.recentActivities.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </PaperSection>
        </Grid>}

        {/* Recent Records */}
        {!isAuditor && <Grid item xs={12} md={6}>
          <PaperSection
            title="Recent Records"
            variant="default"
            action={
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  variant={showFailedOnly ? 'contained' : 'outlined'}
                  color={showFailedOnly ? 'error' : 'inherit'}
                  startIcon={showFailedOnly ? <Error /> : <FilterList />}
                  onClick={() => setShowFailedOnly(!showFailedOnly)}
                >
                  {showFailedOnly ? 'Failed' : 'All'}
                </Button>
              </Stack>
            }
          >
            {displayedRecords.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {showFailedOnly
                    ? 'No failed records found'
                    : 'No records yet. Submit records through your monitoring forms!'}
                </Typography>
              </Box>
            ) : (
              <List sx={{ pt: 0 }} dense>
                {displayedRecords.slice(0, 5).map((item, index) => (
                  <React.Fragment key={item.record.id}>
                    <ListItem
                      alignItems="flex-start"
                      sx={{
                        px: 0,
                        py: 1,
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                      onClick={() =>
                        navigate(
                          `/app/plans/${item.planId}/records/${item.record.collectionId}/entries`
                        )
                      }
                    >
                      <ListItemAvatar sx={{ minWidth: 32 }}>
                        {item.record.passFailStatus === 'fail' ? (
                          <Error sx={{ fontSize: 20, color: 'error.main' }} />
                        ) : item.record.passFailStatus === 'pass' ? (
                          <CheckCircle sx={{ fontSize: 20, color: 'success.main' }} />
                        ) : (
                          <PanoramaFishEyeOutlined
                            sx={{ fontSize: 20, color: 'action.disabled' }}
                          />
                        )}
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.collectionName}
                          </Typography>
                        }
                        secondary={
                          <Stack spacing={0.25}>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {item.planName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {item.record.submitterName} •{' '}
                              {formatRelativeTime(item.record.submittedAt)}
                            </Typography>
                          </Stack>
                        }
                      />
                    </ListItem>
                    {index < displayedRecords.slice(0, 5).length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </PaperSection>
        </Grid>}

        {/* Notifications */}
        {actionableNotifications.length > 0 && (
          <Grid item xs={12}>
            <PaperSection title="Important Notifications" variant="default">
              <Stack spacing={2}>
                {actionableNotifications.map((notification) => (
                  <Alert
                    key={notification.id}
                    severity={notification.severity}
                    action={
                      notification.actionUrl && notification.actionText ? (
                        <Button
                          color="inherit"
                          size="small"
                          onClick={() => navigate(notification.actionUrl!)}
                        >
                          {notification.actionText}
                        </Button>
                      ) : undefined
                    }
                  >
                    {notification.message}
                  </Alert>
                ))}
              </Stack>
            </PaperSection>
          </Grid>
        )}
      </Grid>

      {/* Select Plan for Audit Dialog */}
      <Dialog open={auditDialogOpen} onClose={handleAuditDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Select a Plan for Audit</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which food safety plan you want to audit:
          </Typography>
          <RadioGroup
            value={selectedPlanForAudit}
            onChange={(e) => setSelectedPlanForAudit(e.target.value)}
          >
            {plans.map((plan) => (
              <FormControlLabel
                key={plan.id}
                value={plan.id}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {plan.name}
                    </Typography>
                    {plan.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {plan.description}
                      </Typography>
                    )}
                  </Box>
                }
                sx={{
                  mb: 2,
                  alignItems: plan.description ? 'flex-start' : 'center',
                }}
              />
            ))}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAuditDialogClose}>Cancel</Button>
          <Button
            onClick={handleAuditDialogConfirm}
            variant="contained"
            disabled={!selectedPlanForAudit}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </ContentWrapper>
  );
};

export default Dashboard;
